import { getMenuById, GetMenuById_Params, ICategory, IMenu, IMenuProduct } from '@/services/menu-service';
import { createProduct, CreateProduct_Body, CreateProduct_Response } from '@/services/product-service';
import { AsyncThunkConfig } from '@/store';
import { createUID } from '@/utils/create-uid';
import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { MenuBoardDraft } from '../functions/menu-board-draft';

const initialState: MenuBoardDataSlice = {
  loading: false,
  canPublish: false,
  menu: null,
  board: {},
  sorted: [],
};

const dataSlice = createSlice({
  name: 'menuBoard/data',
  initialState: initialState,
  reducers: {
    updateMenuBoardDataGenericReducer: (state, action: PayloadAction<Partial<MenuBoardDataSlice>>) => {
      const { loading, board, menu, sorted } = action.payload;
      state.loading = loading !== undefined ? loading : state.loading;
      state.board = board !== undefined ? board : state.board;
      state.menu = menu !== undefined ? menu : state.menu;
      state.sorted = sorted !== undefined ? sorted : state.sorted;
      if (state.menu) {
        state.canPublish = true;
        MenuBoardDraft.Set(state.menu._id, { board: state.board, sorted: state.sorted });
      }
    },
    createCategoryReducer: (state, action: PayloadAction<Omit<ICategory, '_id'>>) => {
      const newCategory = { ...action.payload, _id: createUID() };
      state.sorted = [...state.sorted, newCategory];
      state.board = { ...state.board, [newCategory._id]: newCategory.products };
      if (state.menu) {
        state.canPublish = true;
        MenuBoardDraft.Set(state.menu._id, { board: state.board, sorted: state.sorted });
      }
    },
    updateCategoryReducer: (state, action: PayloadAction<ICategory>) => {
      state.sorted = state.sorted.map((item) => (item._id === action.payload._id ? action.payload : item));
      if (state.menu) {
        state.canPublish = true;
        MenuBoardDraft.Set(state.menu._id, { board: state.board, sorted: state.sorted });
      }
    },
    deleteCategoryReducer: (state, action: PayloadAction<ICategory['_id']>) => {
      state.sorted = state.sorted.filter((item) => item._id !== action.payload);
      delete state.board[action.payload];
      if (state.menu) {
        state.canPublish = true;
        MenuBoardDraft.Set(state.menu._id, { board: state.board, sorted: state.sorted });
      }
    },
    updateProductReducer: (state, action: PayloadAction<{ categoryId: ICategory['_id']; productData: IMenuProduct }>) => {
      state.board[action.payload.categoryId] = state.board[action.payload.categoryId].map((item) => (item._id === action.payload.productData._id ? action.payload.productData : item));
      if (state.menu) {
        state.canPublish = true;
        MenuBoardDraft.Set(state.menu._id, { board: state.board, sorted: state.sorted });
      }
    },
    resetCanPublishMenuReducer: (state) => {
      state.canPublish = false;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getMenuByIdThunk.pending, (state, action) => {
      state.loading = true;
    });
    builder.addCase(getMenuByIdThunk.fulfilled, (state, action) => {
      state.loading = false;
      if (action.payload === null) {
        state.menu = null;
        state.board = {};
        state.sorted = [];
        state.canPublish = false;
      } else {
        const { categories, ...rest } = action.payload;
        const draftedData = MenuBoardDraft.Get(rest._id);
        state.canPublish = Boolean(draftedData !== null);
        state.menu = rest;
        state.sorted = draftedData === null ? categories : draftedData.sorted;
        if (draftedData !== null) state.board = draftedData.board;
        else categories.forEach((item) => (state.board[item._id] = item.products));
      }
    });
    builder.addCase(createProductThunk.fulfilled, (state, action) => {
      if (action.payload.productData === null) return;
      const addons = {};
      action.payload.productData.variations.forEach((item) => (addons[item._id] = []));
      state.board[action.payload.categoryId] = [...state.board[action.payload.categoryId], { _id: createUID(), tags: [], disabled: false, product: action.payload.productData, addons: addons }];
      if (state.menu) {
        state.canPublish = true;
        MenuBoardDraft.Set(state.menu._id, { board: state.board, sorted: state.sorted });
      }
    });
  },
});

export const { updateMenuBoardDataGenericReducer, createCategoryReducer, deleteCategoryReducer, updateCategoryReducer, updateProductReducer, resetCanPublishMenuReducer } = dataSlice.actions;
export default dataSlice.reducer;

export interface MenuBoardDataSlice {
  loading: boolean;
  canPublish: boolean;
  menu: Omit<IMenu, 'categories'> | null;
  board: Record<ICategory['_id'], ICategory['products']>;
  sorted: IMenu['categories'];
}

// ASYNC THUNKS
export const getMenuByIdThunk = createAsyncThunk('menuBoard/getMenuByIdThunk', async (params: GetMenuById_Params) => {
  const response = await getMenuById(params);
  return response.data.data;
});

export const createProductThunk = createAsyncThunk<{ categoryId: ICategory['_id']; productData: CreateProduct_Response['data'] }, { categoryId: ICategory['_id']; body: CreateProduct_Body }, AsyncThunkConfig>('menuBoard/createProductThunk', async ({ categoryId, body }) => {
  const response = await createProduct(body);
  return { categoryId, productData: response.data.data };
});
